function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import hexToRgba from "hex-to-rgba";
import times from "lodash/times";
// https://github.com/react-native-community/react-native-hooks/blob/7abd32f513db2cc5ecedb504cb50827685ae6d7b/lib/useLayout.js#L1
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Animated, Platform } from "react-native";
import { PanGestureHandler, State } from "react-native-gesture-handler";
import { Text } from "./Text";
import { View } from "./View";
import { useIsSafeLayoutAnimating } from "../hooks/useSafeLayoutAnimation";
import { Shadow, useTheme } from "../styles";
function useLayout() {
    var _useState = _sliced_to_array(useState({
        x: 0,
        y: 0,
        width: 0,
        height: 0
    }), 2), layout = _useState[0], setLayout = _useState[1];
    // Ref is useful for situations where we need to use the layout data in a closure
    // that was created and is not being updated with the most recent values. By
    // giving it a ref, it can retrieve the most up to date values when it runs.
    // Consider the following example where width will always be 0 but we want
    // ref.current.width will be updated
    //
    // const { width, ref } = useLayout();
    // const otherHandle = useRef(() => {
    //   console.log(width, ref.current.width);
    // });
    //
    // return <View onLayout={onLayout} />;
    var ref = useRef(layout);
    var onLayout = useCallback(function(e) {
        setLayout(e.nativeEvent.layout);
        ref.current = e.nativeEvent.layout;
    }, []);
    return _object_spread({
        onLayout: onLayout,
        ref: ref
    }, layout);
}
var RADIUS = 20;
var BAR_HEIGHT = 10;
var GREATER_COLOR = hexToRgba('#FFFFFF', 0.7);
function getTouchX(param) {
    var width = param.width, delta = param.delta, value = param.value, minimumValue = param.minimumValue;
    var widthDelta = width / delta;
    return (value - minimumValue) * widthDelta;
}
/**
 * A "slider" UI component that allows the user to select a discrete numerical
 * value.
 *
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-125}
 */ export var Slider = /*#__PURE__*/ forwardRef(function(param, forwardedRef) {
    var _param_maximumValue = param.maximumValue, maximumValue = _param_maximumValue === void 0 ? 10 : _param_maximumValue, _param_minimumValue = param.minimumValue, minimumValue = _param_minimumValue === void 0 ? 0 : _param_minimumValue, onValueChange = param.onValueChange, _param_step = param.step, step = _param_step === void 0 ? 1 : _param_step, style = param.style, testID = param.testID, initialValue = param.value;
    var isAnimating = useIsSafeLayoutAnimating();
    var _useState = _sliced_to_array(useState(false), 2), initialized = _useState[0], setInitialized = _useState[1];
    var _useState1 = _sliced_to_array(useState(initialValue || 0), 2), value = _useState1[0], setValue = _useState1[1];
    var _useLayout = useLayout(), ref = _useLayout.ref, width = _useLayout.width, onLayout = _useLayout.onLayout;
    var delta = (maximumValue - minimumValue) / step;
    var touchX = useRef(new Animated.Value(0));
    var theme = useTheme().theme;
    useImperativeHandle(forwardedRef, function() {
        return {
            setValue: function(newValue) {
                setValue(newValue);
                var refWidth = ref.current.width;
                var widthDelta = refWidth / delta;
                Animated.timing(touchX.current, {
                    toValue: (newValue - minimumValue) * widthDelta / step,
                    duration: 200,
                    useNativeDriver: Platform.OS !== 'web'
                }).start();
            }
        };
    });
    useEffect(function() {
        if (width && !initialized) {
            touchX.current.setValue(getTouchX({
                width: width,
                delta: delta,
                minimumValue: minimumValue,
                value: value
            }));
            setInitialized(true);
        }
    }, [
        width,
        initialized,
        delta,
        value,
        minimumValue
    ]);
    var listener = function(event, isEnd) {
        var refWidth = ref.current.width;
        var x = Math.max(0, Math.min(event.nativeEvent.x, refWidth));
        var widthDelta = refWidth / delta;
        var currentValue = Math.round(x / widthDelta) * step + minimumValue;
        if (currentValue !== value) {
            if (onValueChange) {
                onValueChange(currentValue);
            }
            setValue(currentValue);
        }
        if (isEnd) {
            Animated.timing(touchX.current, {
                toValue: (currentValue - minimumValue) * widthDelta / step,
                duration: 200,
                useNativeDriver: Platform.OS !== 'web'
            }).start();
        }
    };
    var onPanGestureEvent = useRef(Animated.event([
        {
            nativeEvent: {
                x: touchX.current
            }
        }
    ], {
        useNativeDriver: true,
        listener: listener
    }));
    var xPos = isAnimating ? Math.min(Math.max(0, getTouchX({
        width: width,
        delta: delta,
        minimumValue: minimumValue,
        value: value
    })), width) : touchX.current.interpolate({
        inputRange: [
            0,
            width
        ],
        outputRange: [
            0,
            width
        ],
        extrapolate: 'clamp'
    });
    var translateX = isAnimating ? xPos - RADIUS : Animated.add(xPos, new Animated.Value(-RADIUS));
    return /*#__PURE__*/ _jsx(View, {
        style: [
            {
                width: '100%'
            },
            style
        ],
        testID: testID,
        "aria-hidden": true,
        children: /*#__PURE__*/ _jsx(PanGestureHandler, {
            minDist: 0,
            onGestureEvent: onPanGestureEvent.current,
            onHandlerStateChange: function(event) {
                if (event.nativeEvent.state === State.END) {
                    listener(event, true);
                }
            },
            children: /*#__PURE__*/ _jsxs(Animated.View, {
                onLayout: onLayout,
                children: [
                    /*#__PURE__*/ _jsxs(View, {
                        style: {
                            backgroundColor: theme.slider.color,
                            borderRadius: BAR_HEIGHT / 2,
                            position: 'absolute',
                            height: BAR_HEIGHT,
                            top: RADIUS - BAR_HEIGHT / 2,
                            width: '100%',
                            overflow: 'hidden'
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Animated.View, {
                                style: [
                                    {
                                        backgroundColor: GREATER_COLOR,
                                        height: '100%',
                                        width: '100%'
                                    },
                                    {
                                        transform: [
                                            {
                                                translateX: xPos
                                            }
                                        ]
                                    }
                                ]
                            }),
                            times(delta, function(i) {
                                return /*#__PURE__*/ _jsx(View, {
                                    style: {
                                        backgroundColor: 'white',
                                        height: 20,
                                        left: i * width / delta,
                                        position: 'absolute',
                                        width: 1
                                    }
                                }, i);
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Animated.View, {
                        style: [
                            Shadow.medium,
                            {
                                alignItems: 'center',
                                backgroundColor: theme.slider.color,
                                borderRadius: RADIUS,
                                height: RADIUS * 2,
                                justifyContent: 'center',
                                width: RADIUS * 2
                            },
                            {
                                transform: [
                                    {
                                        translateX: translateX
                                    }
                                ]
                            }
                        ],
                        children: /*#__PURE__*/ _jsx(Text, {
                            color: "white",
                            text: value.toString(),
                            weight: "bold",
                            style: {
                                lineHeight: RADIUS * 2
                            }
                        })
                    })
                ]
            })
        })
    }, isAnimating.toString());
});
